import axios from 'axios'
import { clearPostLoginRedirect } from '@/lib/auth/postLoginRedirect'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000/api'

// Log API URL in development to help debug configuration issues
if (import.meta.env.DEV) {
  console.log('[API Client] Using API URL:', API_BASE_URL)
  if (!import.meta.env.VITE_API_URL) {
    console.warn('[API Client] VITE_API_URL not set in .env file, using default:', API_BASE_URL)
    console.warn('[API Client] To use staging API, create a .env file with: VITE_API_URL=https://api-staging.deltranslogistics.com/api')
  }
}

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  timeout: 30000, // 30 second timeout
  withCredentials: false, // Set to true if API uses cookies for auth
})

// Request interceptor for auth token
apiClient.interceptors.request.use((config) => {
  if (typeof window !== 'undefined') {
    // Get token from the auth store storage
    const authStorage = localStorage.getItem('auth-storage')
    if (authStorage) {
      try {
        const parsed = JSON.parse(authStorage)
        const token = parsed.state?.token
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
      } catch (error) {
        console.error('Error parsing auth storage:', error)
      }
    }
  }
  return config
})

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle network errors (CORS, timeout, etc.) - don't clear token on network errors
    if (!error.response) {
      // Network error (CORS, timeout, no internet, etc.)
      const errorMessage = error.code === 'ECONNABORTED' 
        ? 'Request timeout - server took too long to respond'
        : error.message || 'Network error - unable to reach server'
      
      console.error('[API Client] Network error:', {
        message: errorMessage,
        code: error.code,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          baseURL: error.config?.baseURL
        }
      })
      
      // Don't clear auth on network errors - token might still be valid
      // The component can handle the error and show a message to the user
      return Promise.reject(error)
    }
    
    // Don't redirect on 401 for public routes (login, 2FA pages, etc.)
    if (error.response?.status === 401) {
      if (typeof window !== 'undefined') {
        const pathname = window.location.pathname
        // List of public routes that don't require authentication
        const publicRoutes = ['/login', '/two-factor', '/forgot-password', '/reset-password', '/signup']
        const isPublicRoute = publicRoutes.some(route => pathname === route || pathname.startsWith(route))
        
        // Check if this is an initialization request (user/me endpoint)
        // During initialization, let the auth store handle the 401 error
        const isInitRequest = error.config?.url?.includes('/user/me')
        
        // Only redirect to login if we're not on a public route AND not during initialization
        // During initialization, the auth store will handle clearing auth state
        if (!isPublicRoute && !isInitRequest) {
          localStorage.removeItem('auth-storage')
          clearPostLoginRedirect()
          window.location.href = '/login'
        }
      }
    }
    // For 403 with 2FA setup required, let it through to be handled by the component
    return Promise.reject(error)
  }
)

export default apiClient

